const PrivacyPolicy = () => {
  return (
    <>
      <div className="container pb-16 lg:pb-28">
        <div className="text-body-2 text-wagelink-dark mt-2">
          <p className="mb-6 lg:mb-8">
            This privacy policy describes the ways Payroll Growth Partners
            LLC (“PGP,” “Payroll Growth Partners”, “we”, “our” or “us”)
            collects, stores, uses, shares, discloses, and protects the personal
            data of WageLink users. Furthermore, it describes the types of
            personal data we collect through our products, services, and online
            presence such as the WageLink mobile app and website. Our privacy
            policy also describes with whom we share your personal information,
            your rights and choices, and how you can contact us to learn more.
            This policy does not apply to third-party websites, products, or
            services, even if they link to PGP sites or services.
          </p>
          <div className="mb-12 lg:mb-20 space-y-4">
            <p className="font-bold">1. About Us</p>
            <p>
              PGP is introducing WageLink products to facilitate paycard
              functionalities for its employees. Employees may use these
              products to manage their paycard balances, view their funds
              available for spending, and view wages earned and wages for being
              sent to paycard. In the event of a lost or stolen card, either the
              app or paycard support will be able to render assistance to the
              employee.
            </p>
          </div>
          <div className="mb-12 lg:mb-20 space-y-4">
            <p className="font-bold">2. Why Do I Need to Read this Policy?</p>
            <p>
              Whenever you visit our websites or use our products and services,
              we may collect your personal data.
            </p>
            <p>
              We explain how this can happen in more detail in Section 3.
              Collected Information and Reasons for Collection. When we say
              “personal data,” we mean information that we know about you or
              which can personally identify you. This includes:
            </p>
            <ul className="list-disc ml-12">
              <li>Name</li>
              <li>Social Security number</li>
              <li>Address</li>
              <li>Email Address</li>
              <li>
                Other types of personal identifiable information described in
                Section 3
              </li>
              <li>Paycard purchase details</li>
            </ul>
            <p>
              This policy outlines the data we gather, how we use it, and your
              rights if you'd like us to handle your personal information
              differently. This policy may be made available in languages other
              than English. If any discrepancies exist between translations, the
              English language version takes precedence.
            </p>
            <p>
              If you have concerns about how we use your personal data, you can
              contact support{" "}
              <a href="mailto:support@wagelink.io" className="underline">
                support@wagelink.io
              </a>
            </p>
          </div>
          <div className="mb-12 lg:mb-20 space-y-4">
            <p className="font-bold">
              3. Type of Collected Information and Reasons for Collection
            </p>
            <p>
              Personal data, or personal information, is any information that
              can be used to identify a person. We may collect, use, and store
              personal data that is collected in different processes or for
              verification. This includes the registration process, card issuing
              process, and KYC process.
            </p>
            <p className="font-bold">Identity Data</p>
            <p>
              This includes information like First name, Last name, Email, Phone
              number, Address, Date of birth, Company, Company ID, Employee ID,
              Company Emails, and Social Security Number. This information is
              required for creating a profile, connecting company information
              for payroll-related services, ordering cards, or connecting
              already existing cards. We also collaborate with AML partners to
              detect and minimize fraudulent activities.
            </p>
            <p className="font-bold">Financial Data</p>
            <p>
              This includes information such as card data required for receiving
              funds such as DDA Number and Card Routing Number. This also
              includes transaction details and history. We use this information
              to process transactions and manage users’ accounts. Many of the
              sensitive information such as card numbers, expiry dates, etc. are
              not stored in our system. Instead, these are held by our
              third-party partners and facilitators.
            </p>
            <p className="font-bold">Location Data</p>
            <p>
              This includes home address and email address. We may collect
              information about your location when you use our services to
              better understand how our users interact with our services.
            </p>
            <p className="font-bold">Technical Data</p>
            <div className="space-y-4">
              <p>This includes:</p>
              <ul className="list-disc ml-12">
                <li>internet protocol (IP) address</li>
                <li>login information</li>
                <li>browser type and version</li>
                <li>time zone setting and location information</li>
                <li>browser plug-in types and versions</li>
                <li>operating system and platform</li>
                <li>devices where the application is running</li>
              </ul>
              <p>
                And other technology and information stored on devices you grant
                us access to. You’re prompted to consent or decline during
                sign-up, when visiting sites, or when otherwise interacting with
                digital content.
              </p>
            </div>
            <p className="font-bold">Profile Data</p>
            <p>
              Username, password, requests made by users for goods or services,
              interests, preferences, feedback, and survey results are all
              profile data that WageLink stores. Sensitive data is encrypted and
              managed in line with strict, legally compliant practices.
            </p>
            <p className="font-bold">Usage Data</p>
            <p>
              Usage of the Wagelink site, app, products, and services are
              examples of usage data that help us understand the user
              experience. Users enter this information and submit it during the
              registration and KYC submission processes. We also collect users'
              information from the WageLink Pay Card waiting list.
            </p>
          </div>
          <div className="mb-12 lg:mb-20 space-y-4">
            <p className="font-bold">
              4. Sources From Which We May Collect Data
            </p>
            <p>
              You may also choose to submit information to us via other methods,
              including:
            </p>
            <p>(i) while using our digital applications,</p>
            <p>(ii) while using websites associated with WageLink,</p>
            <p>(iii) in response to marketing or other communications,</p>
            <p>(iv) through social media or online forums or surveys,</p>
            <p>
              (v) through participation in an offer, program or promotion, or
            </p>
            <p>
              (vi) in connection with an actual or potential business
              relationship with us.
            </p>
          </div>
          <div className="mb-12 lg:mb-20 space-y-4">
            <p className="font-bold">5. How We Disclose Personal Data</p>
            <p>
              We share your Personal Data with trusted entities, as outlined
              below.
            </p>
            <p className="font-bold">Service Providers</p>
            <div className="space-y-4">
              <p>
                We share Personal Data with a limited number of our service
                providers. We have service providers that provide services on
                our behalf, such as identity verification services, website
                hosting, data analysis, information technology and related
                infrastructure, customer service, email delivery, and auditing
                services.
              </p>
              <p>
                Some of the service providers include{" "}
                <a
                  href="https://www.transcard.com/"
                  target="_blank"
                  rel="noreferrer"
                  className="underline"
                >
                  Transcard
                </a>
                <span className=""> as a card provider, </span>
                <a
                  href="https://aws.amazon.com/console/"
                  target="_blank"
                  rel="noreferrer"
                  className=" underline"
                >
                  AWS Cloud
                </a>
                <span className="">
                  {" "}
                  as an infrastructure partner, Azure as a payroll partner, and
                  so on.
                </span>
              </p>
              <p>
                These service providers may need to access Personal Data to
                perform their services. We authorize such service providers to
                use or disclose the Personal Data only as necessary to perform
                services on our behalf or comply with legal requirements.
              </p>
              <p>
                We require such service providers to contractually commit to
                protect the security and confidentiality of the personal data
                they process on our behalf.
              </p>
            </div>
            <p className="font-bold">Business Partners</p>
            <p>
              We share personal data with third-party business partners when
              this is necessary to provide our services to our users. Examples
              of third parties to whom we may disclose personal data for this
              purpose are banks and payment method providers when we provide
              payment processing services, and the professional services firms
              that we partner with that utilize our services.
            </p>
            <p className="font-bold">
              Our Users and Third Parties Authorized by Users
            </p>
            <p>
              We share personal data with users as necessary to maintain a user
              account and provide services. We share data with parties directly
              authorized by a user to receive personal data. The use of personal
              data by an authorized third party is subject to the third party’s
              privacy policy.
            </p>
            <p className="font-bold">Compliance and Harm Prevention</p>
            <p>We share personal data as we believe necessary to:</p>
            <ul className="space-y-4">
              <li>(i) Comply with applicable law or payment method rules,</li>
              <li>(ii) Enforce our contractual rights,</li>
              <li>
                (iii) Protect the rights, privacy, safety, and property of
                Transcard, you, or others, and
              </li>
              <li>
                (iv) Respond to requests from courts, law enforcement agencies,
                regulatory agencies, and other public and government
                authorities, which may include authorities outside your country
                of residence.
              </li>
            </ul>
            <p>
              All user data is only collected after user consent for the
              collection.
            </p>
          </div>
          <div className="mb-12 lg:mb-20 space-y-4">
            <p className="font-bold">
              6. Our Legal Basis For Using Your Personal Data
            </p>
            <p>
              We must have a legal basis (a valid legal reason) for using your
              personal data. Our legal basis will be one of the following:
            </p>
            <p className="font-bold">
              Keeping to our contracts and agreements with you
            </p>
            <p>
              We need certain personal data to provide our services and cannot
              provide them without this personal data.
            </p>
            <p className="font-bold">Legal obligations</p>
            <p>
              In some cases, we have a legal responsibility to collect and store
              your personal data (for example, under anti-money laundering laws
              we must hold certain information about our customers).
            </p>
            <p className="font-bold">Legitimate interests</p>
            <p>
              We sometimes collect and use your personal data because we have a
              legitimate reason to use it and this is reasonable when balanced
              against your human rights and freedoms.
            </p>
            <p className="font-bold">Substantial public interest</p>
            <p>
              In this case, we process your personal data or sensitive personal
              data (sometimes known as special category personal data) to adhere
              to government regulations or guidance. This includes our
              obligation to support you if you are or become a vulnerable
              customer, for instance.
            </p>
            <p className="font-bold">Consent</p>
            <p>
              Where you've agreed to us collecting your personal data, or
              sensitive personal data. For example: when you tick a box to
              indicate you’re happy for us to use your personal data in a
              certain way.
            </p>
            <p>
              We explain more about how we use your personal data in the How we
              use the data users provide section below.
            </p>
          </div>
          <div className="mb-12 lg:mb-20 space-y-4">
            <p className="font-bold">7. How We Use Your Personal Data</p>
            <p>Explore the ways in which we may use your personal data:</p>
            <p className="font-bold">A. Improving our services:</p>
            <p>
              We use your personal data to help us develop and improve our
              current products and services. This allows us to continue to
              provide products and services that our customers want to use.
            </p>
            <p>
              We use your information to provide and improve our services,
              including processing transactions and managing your account. Your
              personal data helps us manage our website and the WageLink app
              (including troubleshooting, data analysis, testing, research,
              statistical, and survey purposes). Furthermore, it helps to make
              sure that content is presented in the most effective way for you
              and your device.
            </p>
            <p>We also use your personal data to:</p>
            <ul className="list-disc ml-12">
              <li className="">
                Verify your identity if you contact our customer support or
                social media teams
              </li>
              <li>
                Allow you to take part in interactive features of our services
              </li>
              <li>Tell you about changes to our services</li>
              <li>
                Help keep our website and the WageLink app safe and secure
              </li>
            </ul>
            <p>
              The legal basis for using personal data to improve our services:
            </p>
            <ul className="list-decimal ml-14">
              <li>Keep the contracts and agreements between you and us</li>
              <li>
                Legitimate interests (to be efficient about how we meet our
                obligations and keep to regulations that apply to us and to
                present content as effectively as possible for you)
              </li>
              <li>
                Prevent criminal activities and fraudulent behaviors within the
                system
              </li>
              <li>Consent (where required by law)</li>
            </ul>
            <p className="font-bold">B. Communication with users:</p>
            <p>
              We use your information to share with you information about your
              account and transactions, as well as information about our
              services and updates. Depending on the information, we may use the
              user’s email or phone number.
            </p>
            <p className="font-bold">Legal basis:</p>
            <p>
              Legitimate interests (to understand how customers use our products
              so we can develop new products and improve the products we
              currently provide)
            </p>
            <p className="font-bold">C. For research and development:</p>
            <p>
              We use your information to improve our services and develop new
              features. We prepare anonymous statistical datasets about our
              customers’ spending patterns:
            </p>
            <ul className="list-disc ml-12">
              <li>For forecasting purposes</li>
              <li>To understand how customers use WageLink</li>
              <li>To comply with governmental requirements and requests</li>
            </ul>
            <p>
              These datasets may be shared internally or externally with other
              partners. We produce these reports using information about you and
              other customers. The information used and shared in this way is
              never personal data and you will never be identifiable from it.
              Anonymous statistical data cannot be linked back to you as an
              individual.
            </p>
            <p>
              For example, some countries have laws that require us to report
              spending statistics and how money enters or leaves each country.
            </p>
            <p className="font-bold">Legal basis:</p>
            <p>
              Legitimate interests (to understand how customers use our products
              so we can develop new products and improve the products we
              currently provide)
            </p>
            <p className="font-bold">D. Compliance and legal purposes:</p>
            <p>
              We may use your information as required by law or to respond to
              legal processes. We use your personal data to check your identity
              to protect against fraud, keep to financial crime laws and confirm
              that you're eligible to use our services. We also use it to help
              us better understand your financial circumstances and manage fraud
              risks related to your WageLink profile.
            </p>
            <p className="font-bold">Legal basis:</p>
            <p>
              Legitimate interests (to develop and improve how we deal with
              financial crime and meet our legal responsibilities)
            </p>
            <p>Legal obligations</p>
          </div>
          <div className="mb-12 lg:mb-20 space-y-4">
            <p className="font-bold">
              8. Automated Decisions About You (In Certain WageLink Products and
              Services)
            </p>
            <p>
              Depending on the WageLink products or services you use, we may
              make automated decisions about you.
            </p>
            <p>
              This means that we may use technology that can evaluate your
              personal circumstances and other factors to predict risks or
              outcomes. This is sometimes known as profiling. We do this for the
              efficient running of our services and to ensure decisions are
              fair, consistent, and based on the right information.
            </p>
            <p>
              When we make an automated decision about you, you have the right
              to ask that it be manually reviewed by a person. You can find out
              more about this in <i>Section 10: What Are My Rights?</i>
            </p>
            <p>
              For example, we may make automated decisions about you that relate
              to
            </p>
            <p className="font-bold">A. Opening accounts:</p>
            <ul className="list-disc ml-12">
              <li>KYC</li>
              <li>Anti-money laundering and sanctions checks</li>
              <li>Identity and address checks</li>
            </ul>
            <p className="font-bold">B. Detecting fraud:</p>
            <ul className="list-disc ml-12">
              <li>
                Monitoring your account to detect fraud and financial crime
              </li>
            </ul>
            <p className="font-bold">Legal basis:</p>
            <p>
              Keeping to contracts and agreements between you and us legal
              obligations
            </p>
            <p>
              Legitimate interests (to develop and improve how we deal with
              financial crime and meet our legal responsibilities)
            </p>
          </div>
          <div className="mb-12 lg:mb-20 space-y-4">
            <p className="font-bold">
              9. How do you use my personal data for marketing?
            </p>
            <p>
              If you sign up to our services, and where national laws allow,
              we'll assume you want us to contact you by mail, push
              notification, email and text message with information about
              WageLink products, services, offers and promotions. Where national
              laws require us to get your consent to send marketing messages,
              we'll do so in advance.
            </p>
            <p>
              We use your personal data to personalize marketing messages about
              our products and services so they are more relevant and
              interesting to you (where allowed by law). This may include
              analyzing how you use our services and your transactions.
            </p>
            <p>
              We won't pass your details on to any organizations outside the
              WageLink group for their marketing purposes without your
              permission. You can find out more in the How we share your
              information section below.
            </p>
            <p className="font-bold">Legal basis:</p>
            <p>
              Consent (where we are required by law to collect your consent);
            </p>
            <p>
              Legitimate interests (to send you marketing and to provide
              information relevant to your interests).
            </p>
          </div>
          <div className="mb-12 lg:mb-20 space-y-4">
            <p className="font-bold">10. Your Rights</p>
            <p>Your rights include:</p>
            <p className="font-bold">
              A. You have the right to be told how we use your personal data.
            </p>
            <p>
              We provide this privacy policy to explain how we use your personal
              data.
            </p>
            <p>
              If you ask, we’ll provide a copy of the personal data we hold
              about you. We can’t give you any personal data about other people,
              personal data that is linked to an ongoing criminal or fraud
              investigation, or personal data that is linked to settlement
              negotiations with you. We also won't provide you with any
              communication we've had with our legal advisers.
            </p>
            <p className="font-bold">
              B. You can ask us to correct your personal data if you think it's
              wrong.
            </p>
            <p>
              You can have incomplete or inaccurate personal data corrected.
              Before we update your file, we may need to check the accuracy of
              the new personal data you have provided.
            </p>
            <p className="font-bold">
              C. You can ask us to delete your personal data.
            </p>
            <p>You can ask us to delete your personal data if:</p>
            <p>✓ there's no good reason for us to continue using it</p>
            <p>
              ✓ you gave us consent (permission) to use your personal data and
              you have now withdrawn that consent
            </p>
            <p>✓ you have objected to us using your personal data</p>
            <p>✓ we have used your personal data unlawfully</p>
            <p>✓ the law requires us to delete your personal data</p>
            <p>
              We will attempt to comply, but may not be able to agree to your
              request. As a regulated financial services provider, we must keep
              certain customer personal data even when you ask us to delete it
              (we've explained this in more detail below).
            </p>
            <p>
              If you've closed your WageLink account, we may not be able to
              delete your entire file because these regulatory and legal
              responsibilities take priority. We’ll always let you know if we
              can't delete your personal data.
            </p>
            <p className="font-bold">
              D. You can object to us processing your personal data for
              marketing purposes.
            </p>
            <p>
              You can tell us to stop using your personal data, including
              profiling you, for marketing.
            </p>
            <p className="font-bold">
              E. You can object to us processing other personal data (if we’re
              using it for legitimate interests).
            </p>
            <p>
              If our legal basis for using your personal data is 'legitimate
              interests' and you disagree with us using it, you can object.
              However, if there is an overriding reason why we need to use your
              personal data, we will not accept your request. If you object to
              us using personal data which we need in order to provide our
              services, we may need to close your account as we won’t be able to
              provide the services.
            </p>
            <p className="font-bold">
              F. You can ask us to restrict how we use your personal data.
            </p>
            <p>You can ask us to suspend using your personal data if:</p>
            <p>✓ You want us to investigate whether it’s accurate</p>
            <p>
              ✓ Our use of your personal data is unlawful but you don’t want us
              to delete it
            </p>
            <p>
              ✓ We no longer need your personal data, but you want us to
              continue holding it for you in connection with a legal claim
            </p>
            <p>
              ✓ You have objected to us using your personal data (see above),
              but we need to check whether we have an overriding reason to use
              it
            </p>
            <p className="font-bold">
              G. You can ask us to transfer personal data to you or another
              company.
            </p>
            <p>
              If we can, and are allowed to do so under regulatory requirements,
              we’ll provide your personal data in a structured, commonly used,
              machine-readable format.
            </p>
            <p className="font-bold">H. You can withdraw your permission.</p>
            <p>
              If you’ve given us the consent we need to use your personal data,
              you can withdraw it at any time by changing your privacy settings
              in the Zebec app or sending an email to{" "}
              <a href="mailto:support@wagelink.io" className="underline">
                support@wagelink.io
              </a>
            </p>
            <p>
              (Please note, it will have been lawful for us to use the personal
              data up to the point you withdraw your permission.)
            </p>
            <p className="font-bold">
              I. You can ask us to carry out a human review of an automated
              decision we make about you.
            </p>
            <p>
              If we make an automated decision about you that significantly
              affects you, you can ask us to carry out a manual review of this
              decision.
            </p>
            <p>
              Your ability to exercise these rights will depend on a number of
              factors. Sometimes, we won’t be able to agree to your request (for
              example, if we have a legitimate reason for not doing so or the
              right does not apply to the particular information we hold about
              you).
            </p>
          </div>
          <div className="mb-12 lg:mb-20 space-y-4">
            <p className="font-bold">11. How do I exercise my rights?</p>
            <p>
              To exercise any of your rights set out in the previous section,
              you can contact us through the Zebec app or send us an email at{" "}
              <a href="mailto:support@wagelink.io" className="underline">
                support@wagelink.io.
              </a>
            </p>
            <p>
              For security reasons, we can't deal with your request if we’re not
              sure of your identity, so we may ask you for proof of ID.
            </p>
            <p>
              If a third party exercises one of these rights on your behalf, we
              may need to ask for proof that they’ve been authorized to act on
              your behalf.
            </p>
            <p>
              When you exercise one of your rights or update your privacy
              settings in the Zebec app, it may take us up to one month to
              respond or implement your changes.
            </p>
            <p>
              Wagelink will usually not charge you a fee when you exercise your
              rights. However, we’re allowed by law to charge a reasonable fee
              or refuse to act on your request if it is manifestly unfounded or
              excessive.
            </p>
            <p>
              Wagelink will not discriminate against you in any way because you
              exercise any rights conferred by the WageLink Privacy Policy.
            </p>
            <p>
              If you are unhappy with how we have handled your personal
              information, you can file a formal complaint by sending us an
              email to{" "}
              <a href="mailto:support@wagelink.io" className="underline">
                support@wagelink.io.
              </a>
            </p>
          </div>
          <div className="mb-12 lg:mb-20 space-y-4">
            <p className="font-bold">
              12. Who We Share Your Personal Data With, and Why
            </p>
            <p className="font-bold">A. Third-party service providers</p>
            <p>
              We may share your information with third-party service providers
              who assist us in providing our services. We provide user
              information to the Unbanked system (one of our third-party service
              providers), which is required to complete these processes. We may
              share your personal data with our partners to provide certain
              services you’ve asked us for.
            </p>
            <p>
              We’ll only share your data in this way if you’ve asked for the
              relevant service or if it’s provided as part of our membership
              plans. From time to time, we may work with other partners to offer
              you co-branded services or promotional offers, and we’ll share
              some of your personal data with those partners. We will always
              ensure you understand how our partners and we process your
              personal data for these purposes.
            </p>
            <p className="font-bold">B. Law enforcement:</p>
            <p>
              We may share your information with law enforcement in response to
              a subpoena or other legal process or to protect our users' and
              services' safety and security. We also share your personal data
              with other financial institutions, government authorities, law
              enforcement authorities, tax authorities, companies and fraud
              prevention agencies.
            </p>
            <p>
              The purpose of sharing your information with these entities is to
              check your identity, protect against fraud, observe tax and
              anti-money laundering laws, and confirm that you’re eligible to
              use our products and services. If fraud is detected, you could be
              refused certain services by WageLink or other companies.
            </p>
            <p>
              We may also need to share your personal data with other
              third-party organizations or authorities:
            </p>
            <ul className="list-decimal ml-14">
              <li>
                If we have to do so under any law or regulation if we sell our
                business or credit portfolio
              </li>
              <li>
                To enforce our rights (and those of customers or others) in
                connection with legal requirements
              </li>
            </ul>
          </div>
          <div className="mb-12 lg:mb-20 space-y-4">
            <p className="font-bold">13. How We Protect Your Personal Data</p>
            <p>
              We recognize the importance of protecting and managing your
              personal data. Any personal data we process will be treated with
              the utmost care and security. This section sets out some of the
              security measures we have in place.
            </p>
            <p>We use a variety of physical and technical measures to:</p>
            <ul className="list-disc ml-12">
              <li>keep your personal data safe</li>
              <li>prevent unauthorized access to your personal data</li>
              <li>
                make sure your personal data is not improperly used or disclosed
              </li>
            </ul>
            <p>
              Electronic data and databases are stored on secure computer
              systems with control over access to information using both
              physical and electronic means. Our staff receives data protection
              and information security training. We have detailed security and
              data protection policies that staff must follow when they handle
              your personal data.
            </p>
            <p>
              While we take all reasonable steps to ensure that your personal
              data will be kept secure from unauthorized access, we cannot
              guarantee it will be secure during transmission by you to our app,
              website, or other services.
            </p>
            <p>
              We use HTTPS (HTTP Secure), where the communication protocol is
              encrypted through Transport Layer Security for secure
              communication over networks for all our app, web, and
              payment-processing services.
            </p>
            <p>
              If you use a password for the WageLink app or our website, you
              will need to keep this password confidential. Please do not share
              it with anyone.
            </p>
            <p>
              When you use our public services, which include our social network
              accounts and Community forum, do not share any personal data that
              you don't want to be seen, collected, or used by other customers.
              As you yourself have shared it, this personal data will become
              publicly available.
            </p>
          </div>
          <div className="mb-12 lg:mb-20 space-y-4">
            <p className="font-bold">14. How Long We Keep Your Personal Data</p>
            <p>
              We’ll generally keep your data for six years after our business
              relationship ends or such period as may be required by applicable
              local laws.
            </p>
            <p>
              We must keep your personal data for this long by anti-money
              laundering laws. We may keep your personal data for longer because
              of a potential or ongoing court claim or for another legal reason.
            </p>
          </div>
          <div className="mb-12 lg:mb-20 space-y-4">
            <p className="font-bold">
              15. How We Keep You Updated on Our Use of Your Personal Data
            </p>
            <p>
              We may change this privacy policy from time to time to reflect new
              services, changes in our personal data practices, or relevant
              laws.
            </p>
            <p>
              The “Last Revised Date” legend at the top of this privacy policy
              indicates when this privacy policy was last revised. Any changes
              are effective when we post the revised privacy policy on our
              services. We may provide you with disclosures and alerts regarding
              the privacy policy or personal data collected by posting them on
              our website and, if you are a user, by contacting you through
              email address and/or the physical address listed in your WageLink
              account.
            </p>
            <p>
              In the case that we make modifications to this privacy policy, we
              will notify you by revising the date at the top of this privacy
              policy. In some cases, we may provide you with additional notice
              (such as adding a statement to our homepage or sending you an
              email notification).
            </p>
            <p>
              We encourage you to review the privacy policy periodically to stay
              informed about how we are protecting your information. 
            </p>
          </div>
          <div className="space-y-4">
            <p>Contact us</p>
            <p>
              If you have any questions or concerns about this privacy policy or
              our privacy practices, please contact us at:
            </p>
            <p>
              email:{" "}
              <a href="mailto:support@wagelink.io" className="underline">
                support@wagelink.io
              </a>
            </p>
            <p>helpline: + (310) 601-8444</p>
            <p>30 N. Gould St.</p>
            <p>Ste. N. Sheridan,</p>
            <p>WY 82801</p>
          </div>
        </div>
      </div>
    </>
  );
};

export default PrivacyPolicy;
